import { Bot, Calendar, CheckCircle2, ChevronRight, Circle, Phone, Smartphone, Users } from 'lucide-react'
import { APP_STORE_URL, MAC_APP_URL, PLAY_STORE_URL } from '@/lib/appsMobiles'
import { useTranslation } from 'react-i18next'
import { useProfile } from '@/api/profile'
import { useSubscriptionStatus } from '@/api/subscription'
import { useCalendars } from '@/api/calendars'
import { useMiseEnRoute } from '@/hooks/useMiseEnRoute'
import { cn } from '@/lib/utils'
import { TrialUsageCard } from './overview/TrialUsageCard'

interface Props {
  onTabChange: (tab: string) => void
}

/**
 * « Commencer » : la page des premiers jours (modèle Aircall). Elle n'existe
 * que pendant l'essai : ce qu'il reste à faire, et les minutes d'essai.
 * Après l'essai, l'administration s'ouvre sur Agents IA.
 */
export function OverviewView({ onTabChange }: Props) {
  const { t } = useTranslation()
  const { data: profile } = useProfile()
  const companyId = profile?.company
  const { data: subscriptionStatus } = useSubscriptionStatus()
  const { pas: obligatoires } = useMiseEnRoute()
  const { data: calendriers = [] } = useCalendars(companyId)

  const isTrialing = subscriptionStatus?.subscription?.status === 'trialing'

  const pas: { id: string; icon: typeof Phone; fait: boolean; label: string; cta: string; optionnel?: boolean; magasins?: boolean }[] = [
    { id: 'numbers', icon: Phone, fait: obligatoires.numero, label: t('manager.start.number', 'Avoir un numéro'), cta: t('manager.start.numberCta', 'Choisir un numéro') },
    { id: 'ai-agents', icon: Bot, fait: obligatoires.agent, label: t('manager.start.agent', 'Créer un agent IA qui répond'), cta: t('manager.start.agentCta', 'Créer un agent') },
    { id: 'team', icon: Users, fait: obligatoires.equipe, label: t('manager.start.team', 'Inviter un collègue'), cta: t('manager.start.teamCta', 'Inviter') },
    { id: 'calendars', icon: Calendar, fait: calendriers.length > 0, label: t('manager.start.calendar', 'Connecter un agenda pour les rendez-vous'), cta: t('manager.start.calendarCta', 'Connecter'), optionnel: true },
    { id: 'app', icon: Smartphone, fait: obligatoires.app, label: t('manager.start.app', "Installer l'app sur votre téléphone et votre Mac"), cta: '', magasins: true },
  ]
  const faits = pas.filter((p) => p.fait).length

  return (
    <div className="h-full overflow-y-auto">
      <div className="px-6 lg:px-8 py-6 space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">{t('manager.start.title', 'Commencer')}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('manager.start.subtitle', '{{done}} sur {{total}} pas faits. Le reste se règle en quelques minutes.', { done: faits, total: pas.length })}
          </p>
        </div>

        {companyId && isTrialing && <TrialUsageCard companyId={companyId} isTrialing={isTrialing} />}

        <div className="rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
          {pas.map((p) => {
            const Icone = p.icon
            return (
              <div key={p.id} className="flex items-center gap-3 px-4 h-12">
                {p.fait
                  ? <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                  : <Circle className="h-5 w-5 text-slate-300 dark:text-slate-600 flex-shrink-0" />}
                <Icone className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span className={cn('flex-1 text-sm', p.fait ? 'text-slate-500 line-through decoration-slate-300' : 'text-slate-900 dark:text-white')}>
                  {p.label}
                  {p.optionnel && !p.fait && <span className="ml-2 text-xs text-slate-400">{t('manager.start.optional', 'facultatif')}</span>}
                </span>
                {!p.fait && p.magasins && (
                  <span className="flex items-center gap-2">
                    <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400">App Store</a>
                    <span className="text-slate-300">·</span>
                    <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400">Google Play</a>
                    <span className="text-slate-300">·</span>
                    <a href={MAC_APP_URL} className="text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400">Mac</a>
                  </span>
                )}
                {!p.fait && !p.magasins && (
                  <button type="button" onClick={() => onTabChange(p.id)} className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400">
                    {p.cta}
                    <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
